import { CuriosityAutocomplete, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  ValueEditorInput,
  ValueEditorNumberInput,
  ValueEditorNumberRange,
  ValueEditorSelect,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CommodityFilterField } from '@/generated/gql/graphql';
import { CommodityQueryFieldGroup } from '~/common/types';
import {
  listOperators,
  operators,
  queryFieldFactory,
  renderPercentageEditor,
  UseDataGridQueryBuilderConfig,
} from '~/components/DataGridQueryBuilder';

const NAMESPACE_MAP = {
  [CommodityFilterField.id]: CuriosityNamespaceType.COMMODITY,
  [CommodityFilterField.supplierId]: CuriosityNamespaceType.SUPPLIER,
  [CommodityFilterField.sourceSystemId]: CuriosityNamespaceType.SOURCE_SYSTEM,
  [CommodityFilterField.commodityGroupId]: CuriosityNamespaceType.COMMODITY_GROUP,
  [CommodityFilterField.primaryCommodityGroupId]: CuriosityNamespaceType.COMMODITY_GROUP,
};

export const useCommodityDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation();

  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'Base.commodity' });
    return {
      fields: [
        // ---------- COMMODITY ----------
        createField({
          name: CommodityFilterField.id,
          grp: CommodityQueryFieldGroup.commodity,
          operators: operators.id,
        }),
        createField({
          name: CommodityFilterField.externalId,
          grp: CommodityQueryFieldGroup.commodity,
          operators: operators.text,
        }),
        createField({
          name: CommodityFilterField.name,
          grp: CommodityQueryFieldGroup.commodity,
          operators: operators.text,
        }),
        createField({
          name: CommodityFilterField.description,
          grp: CommodityQueryFieldGroup.commodity,
          operators: operators.text,
        }),
        // ---------- SUPPLIER ----------
        createField({
          name: CommodityFilterField.supplierId,
          grp: CommodityQueryFieldGroup.supplier,
          operators: operators.id,
        }),
        // ---------- PERFORMANCE ----------
        createField({
          name: CommodityFilterField.deliveryOnTimePercentage,
          grp: CommodityQueryFieldGroup.performance,
          operators: operators.num,
        }),
        createField({
          name: CommodityFilterField.correctOrderPercentage,
          grp: CommodityQueryFieldGroup.performance,
          operators: operators.num,
        }),
        createField({
          name: CommodityFilterField.deviatingPricePercentage,
          grp: CommodityQueryFieldGroup.performance,
          operators: operators.num,
        }),
        createField({
          name: CommodityFilterField.deviatingQuantityPercentage,
          grp: CommodityQueryFieldGroup.performance,
          operators: operators.num,
        }),
        createField({
          name: CommodityFilterField.deviatingPriceQuantityPercentage,
          grp: CommodityQueryFieldGroup.performance,
          operators: operators.num,
        }),
        createField({
          name: CommodityFilterField.leadTime,
          grp: CommodityQueryFieldGroup.performance,
          operators: operators.num,
        }),
        // ---------- ORDERS ----------
        createField({
          name: CommodityFilterField.orderVolumeYear,
          grp: CommodityQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityFilterField.orderCountYear,
          grp: CommodityQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityFilterField.orderVolume,
          grp: CommodityQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityFilterField.orderCount,
          grp: CommodityQueryFieldGroup.order,
          operators: operators.num,
        }),
        // ---------- COMMODITY GROUP ----------
        createField({
          name: CommodityFilterField.primaryCommodityGroupId,
          grp: CommodityQueryFieldGroup.commodityGroup,
          operators: operators.textAuto,
        }),
        createField({
          name: CommodityFilterField.commodityGroupId,
          grp: CommodityQueryFieldGroup.commodityGroup,
          operators: operators.idAuto,
        }),
        // ---------- SOURCE SYSTEM ----------
        createField({
          name: CommodityFilterField.sourceSystemId,
          grp: CommodityQueryFieldGroup.sourceSystem,
          operators: operators.idAuto,
        }),
      ],
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          case CommodityFilterField.id:
          case CommodityFilterField.supplierId:
          case CommodityFilterField.primaryCommodityGroupId:
          case CommodityFilterField.commodityGroupId:
          case CommodityFilterField.sourceSystemId:
            return (
              <CuriosityAutocomplete
                ns={NAMESPACE_MAP[field]}
                multiple={listOperators[operator]}
                simplifyValue
                {...otherProps}
              />
            );

          //

          case CommodityFilterField.externalId:
          case CommodityFilterField.description:
            if (listOperators[operator]) {
              return <ValueEditorSelect {...otherProps} multiple={true} freeSolo />;
            }
            return <ValueEditorInput {...otherProps} />;
          //
          case CommodityFilterField.deliveryOnTimePercentage:
          case CommodityFilterField.correctOrderPercentage:
          case CommodityFilterField.deviatingPricePercentage:
          case CommodityFilterField.deviatingQuantityPercentage:
          case CommodityFilterField.deviatingPriceQuantityPercentage:
            return renderPercentageEditor({ operator, otherProps });

          //
          case CommodityFilterField.leadTime:
          case CommodityFilterField.orderVolume:
          case CommodityFilterField.orderVolumeYear:
          case CommodityFilterField.orderCount:
          case CommodityFilterField.orderCountYear:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }
            return <ValueEditorNumberInput min={0} {...otherProps} />;
          default:
            return null;
        }
      },
    };
  }, [t]);
};
